import {AxiosRequestConfig} from 'axios'
const auth_token_key = process.env.REACT_APP_AUTH_TOKEN_KEY!

// Flag to ensure interceptors are only added once
let isAxiosSetup = false

export function setupAxios(axios: any) {
  // Prevent adding interceptors multiple times
  if (isAxiosSetup) {
    return
  }
  
  axios.defaults.headers.Accept = 'application/json'
  axios.interceptors.request.use(
    (request: AxiosRequestConfig) => {
      // In development only: log request method and url to help debugging
      if (process.env.NODE_ENV !== 'production') {
        console.info('axios request:', request.method?.toUpperCase(), request.url)
      }
      
      // Get fresh token on each request
      const freshToken = localStorage.getItem(auth_token_key)
      if (freshToken && request.headers) {
        request.headers.Authorization = `Bearer ${freshToken}`
      }
      
      return request
    },
    (err: any) => {
      console.error('axios request error:', err)
      return Promise.reject(err)
    }
  )
  // Log responses and errors globally to surface server issues
  axios.interceptors.response.use(
    (response: any) => {
      return response
    },
    (error: any) => {
      // Handle 401 Unauthorized - redirect to login
      if (error.response?.status === 401) {
        console.warn('401 Unauthorized - redirecting to login')
        // Clear token and redirect to login
        localStorage.removeItem(auth_token_key)
        window.location.href = '/auth/login'
        return Promise.reject(error)
      }
      
      // Handle 500 errors gracefully for chatbot endpoints
      if (error.response?.status === 500 && error.config?.url?.includes('/chatbot/')) {
        console.warn('Chatbot API error - returning empty response')
        // Return a mock successful response for chatbot endpoints
        return Promise.resolve({
          data: {
            success: true,
            data: {
              responses: []
            }
          }
        })
      }
      
      // Handle other 500 errors gracefully
      if (error.response?.status === 500) {
        // Chat API 500 — reject without logging; chat UI shows friendly message
        if (error.config?.url?.includes('/api/chat/')) {
          return Promise.reject(error)
        }
        console.warn('Server error 500:', error.config?.url, error.response?.data)
        // Keep the server body for payroll, grades, and ZAD so the screen can show it.
        if (
          error.config?.url?.includes('/payroll/') ||
          error.config?.url?.includes('/grades/') ||
          error.config?.url?.includes('/api/zad/')
        ) {
          return Promise.reject(error)
        }
        // For other 500 errors, still reject but with better error message
        return Promise.reject(new Error('Server temporarily unavailable'))
      }

      // Chat API may not be implemented yet (404) — reject without logging to avoid noise
      if (error.response?.status === 404 && error.config?.url?.includes('/api/chat/')) {
        return Promise.reject(error)
      }

      // 403 on /api/users/online (non-admin) or /api/chat/ (e.g. messages permission) — reject without logging
      if (error.response?.status === 403 && (error.config?.url?.includes('/api/users/online') || error.config?.url?.includes('/api/chat/'))) {
        return Promise.reject(error)
      }

      // 503 on AI chat = "AI chat is not configured" — handled in chat panel; no console noise
      if (error.response?.status === 503 && error.config?.url?.includes('/api/chat/ai/')) {
        return Promise.reject(error)
      }
      
      console.error('axios response error:', error.config?.url, error.response?.status, error.response?.data || error.message)
      return Promise.reject(error)
    }
  )
  
  // Mark as setup to prevent duplicate interceptors
  isAxiosSetup = true
}
